/**
 * ResonantArtifactTemplate — Isolated Phrase
 * ---------------------------------------------------------------
 * One emotionally loaded phrase. Held. Surrounded by darkness.
 * Slow ambient breath, faint gold border, archival end-frame.
 * Used for "Resonant Moment" exports — recognition, not analysis.
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import type { SimulationContent } from "@/lib/contentGenerator";
import { FPS, PALETTE, isResonant } from "../utils/timing";
import { BackgroundField } from "../components/BackgroundField";
import { CTAFrame } from "../components/CTAFrame";

interface Props {
  sim: SimulationContent;
  /** Override the phrase — otherwise pulled from first resonant turn. */
  phrase?: string;
  /** Source label, e.g. "Workplace · Invalidation". */
  source?: string;
}

const HOLD_FRAMES = Math.round(FPS * 5); // 5s isolated hold
const CTA_FRAMES = Math.round(FPS * 3);

export const ResonantArtifactTemplate: React.FC<Props> = ({ sim, phrase, source }) => {
  const focal =
    phrase ??
    sim.dialogue.find((t) => isResonant(t.text))?.text ??
    sim.dialogue.find((t) => t.spiral)?.text ??
    sim.dialogue[0]?.text ??
    "";

  const sourceLabel =
    source ??
    `${sim.categoryLabel} · ${sim.archetypeLabel}`;

  const frame = useCurrentFrame();
  // Slow ambient breath on the field
  const breath = interpolate(frame % 180, [0, 90, 180], [0.85, 1, 0.85]);

  // Phrase fade
  const reveal = interpolate(frame, [0, 36], [0, 1], { extrapolateRight: "clamp" });
  const lift = interpolate(frame, [0, 42], [12, 0], { extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ backgroundColor: PALETTE.bg }}>
      <BackgroundField isolate={0.6} />

      <Sequence durationInFrames={HOLD_FRAMES}>
        <AbsoluteFill
          style={{
            padding: "200px 100px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <div
            style={{
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 16,
              letterSpacing: "0.5em",
              textTransform: "uppercase",
              color: PALETTE.goldSoft,
              opacity: 0.8 * breath,
              marginBottom: 36,
            }}
          >
            Resonant Moment
          </div>

          {/* The phrase — isolated, held */}
          <div
            style={{
              maxWidth: 880,
              padding: "44px 48px",
              border: `1px solid ${PALETTE.goldSoft}`,
              borderRadius: 4,
              background: "rgba(0,0,0,0.35)",
              boxShadow: `0 0 80px rgba(201,169,106,${0.10 * breath})`,
              textAlign: "center",
              fontFamily:
                'Georgia, "Times New Roman", serif',
              fontSize: 60,
              fontStyle: "italic",
              fontWeight: 400,
              lineHeight: 1.3,
              color: PALETTE.silver,
              opacity: reveal,
              transform: `translateY(${lift}px)`,
              letterSpacing: "-0.005em",
            }}
          >
            "{focal}"
          </div>

          <div
            style={{
              marginTop: 44,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              fontSize: 16,
              letterSpacing: "0.42em",
              textTransform: "uppercase",
              color: PALETTE.textDim,
              opacity: 0.7,
            }}
          >
            {sourceLabel}
          </div>
        </AbsoluteFill>
      </Sequence>

      <Sequence from={HOLD_FRAMES} durationInFrames={CTA_FRAMES}>
        <CTAFrame preset="archived" />
      </Sequence>
    </AbsoluteFill>
  );
};

export function resonantArtifactDurationFrames(): number {
  return HOLD_FRAMES + CTA_FRAMES;
}
